import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Sparkles } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { useMe } from '@/auth/useAuth';
import { OutOfStarsSheet } from '@/components/OutOfStarsSheet';
import { api } from '@/lib/api';
import { getLiveStudyExperience } from '@/pages/try/studyExperience/mode';

import { STUDY_DOCUMENTS_QUERY_KEY } from './studyApi';
import {
  analyseButtonLabel,
  createStudyAnalysis,
  quoteStudyAnalysis,
  type StudyAnalysis,
  type StudyPageRef,
  type StudyNotesDepth,
} from './studyAnalysisApi';
import {
  ANALYSIS_LOW_BALANCE,
  ANALYSIS_NOT_FOR_SCHOOL,
  analysisErrorMessage,
  analysisUnavailable,
  errorCode,
} from './studyAnalysisErrors';
import { useStudyAvailability } from './studyAvailability';
import { useStudyRecoveryScope } from './useStudyRecoveryScope';

/**
 * Confirm step (study-capture-analyse-prd.md §2, §6): page thumbnails, the server price on the
 * button and a free "Just save it". The price comes from the quote endpoint; the create call
 * sends that price as `expected_stars` with one idempotency key that stays the same across
 * retries of the same pages, so a repeated tap never charges twice.
 */

export interface ConfirmPage extends StudyPageRef {
  previewUrl?: string | null;
}

const OUT_OF_STARS_BODY =
  'Page analysis needs Stars, and your family’s Star tank is empty. No Stars were taken, and your material is saved.';

function pagesKey(pages: StudyPageRef[]) {
  return pages.map((page) => `${page.document_id}:${page.page_number}`).join(',');
}

const IDEMPOTENCY_STORAGE_PREFIX = 'airbotix.study.analysis-create.';

/**
 * The create key must survive a remount: if the create response is lost and the learner reopens
 * the confirm step, the same pages must send the same key so the server does not charge twice.
 * Storage can be blocked; then the key lives only in memory for this component.
 */
function readCreateKey(owner: string, pages: string): string | null {
  try {
    return window.sessionStorage.getItem(`${IDEMPOTENCY_STORAGE_PREFIX}${owner}:${pages}`);
  } catch {
    return null;
  }
}

function writeCreateKey(owner: string, pages: string, key: string | null) {
  try {
    const name = `${IDEMPOTENCY_STORAGE_PREFIX}${owner}:${pages}`;
    if (key) window.sessionStorage.setItem(name, key);
    else window.sessionStorage.removeItem(name);
  } catch {
    // Blocked storage: the in-memory key still covers retries in this component.
  }
}

export function StudyAnalyseConfirm({
  pages,
  compact = false,
  onAnalysed,
  onSave,
  onPrepare,
  actionLabel,
  tone = 'primary',
}: {
  pages: ConfirmPage[];
  /** A single row without thumbnails, for the material preview. */
  compact?: boolean;
  onAnalysed: (analysis: StudyAnalysis) => void;
  /** Free "Just save it". Hidden when not given. */
  onSave?: () => void;
  /** Called instead of showing an error when the page images are not uploaded yet. */
  onPrepare?: () => void;
  /** Button text in place of "Analyse N pages", such as "Analyse first"; the price is added. */
  actionLabel?: string;
  /** `secondary` when the button sits in a list next to a primary action (Practice tab). */
  tone?: 'primary' | 'secondary';
}) {
  const queryClient = useQueryClient();
  const reasonId = useId();
  const me = useMe();
  const familyId = me.data?.kind === 'kid' ? me.data.family_id : null;
  const availability = useStudyAvailability();
  const institutionFunded = availability.data?.paid_ai_funding === 'institution_pool';
  const school =
    availability.data?.learner_source === 'institution' &&
    availability.data.paid_ai_funding !== 'institution_pool';
  // Wait for the learner source so a school account never asks for a price (§6). If the
  // availability call fails, the server still refuses the quote for a school account.
  const canPrice = !availability.isPending && !school;
  const refs = useMemo(
    () => pages.map(({ document_id, page_number }) => ({ document_id, page_number })),
    [pages],
  );
  const [notesDepth, setNotesDepth] = useState<StudyNotesDepth>('in_depth');
  const key = `${pagesKey(refs)}:notes:${notesDepth}`;
  const { owner: kidId, scope, isCurrent } = useStudyRecoveryScope(`confirm:${key}`);
  const idempotencyKey = useRef<{ pages: string; key: string } | null>(null);
  const [outOfStars, setOutOfStars] = useState(false);

  const quote = useQuery({
    queryKey: ['study-analysis-quote', kidId, key, scope],
    queryFn: async () => {
      if (!isCurrent(scope)) throw new Error('Open your current material again.');
      const result = await quoteStudyAnalysis(refs, notesDepth);
      if (!isCurrent(scope)) throw new Error('Open your current material again.');
      return result;
    },
    enabled: Boolean(kidId) && refs.length > 0 && canPrice,
    retry: false,
    staleTime: 30_000,
  });
  const liveExperience = getLiveStudyExperience();
  const wallet = useQuery({
    queryKey: institutionFunded
      ? ['study', 'institution-payer', kidId]
      : liveExperience
        ? ['study', 'teacher-experience', 'payer']
        : ['wallet', familyId],
    queryFn: () =>
      api<{ stars_balance: number }>(
        institutionFunded
          ? '/study/me/funding'
          : liveExperience
            ? '/study/teacher-experience/payer'
            : `/families/${familyId}/wallet`,
      ),
    enabled: (Boolean(familyId) || institutionFunded) && canPrice,
    retry: false,
  });

  const create = useMutation({
    mutationFn: (request: { scope: string; expectedStars: number }) => {
      if (!isCurrent(request.scope)) throw new Error('Open your current material again.');
      const owner = kidId ?? 'anonymous';
      const identity = JSON.stringify([owner, key]);
      let entry = idempotencyKey.current;
      if (!entry || entry.pages !== identity) {
        entry = { pages: identity, key: readCreateKey(owner, key) ?? crypto.randomUUID() };
        idempotencyKey.current = entry;
        writeCreateKey(owner, key, entry.key);
      }
      return createStudyAnalysis({
        pages: refs,
        expected_stars: request.expectedStars,
        idempotency_key: entry.key,
        notes_depth: notesDepth,
      });
    },
    onSuccess: async (analysis, request) => {
      if (!isCurrent(request.scope)) return;
      writeCreateKey(kidId ?? 'anonymous', key, null);
      idempotencyKey.current = null;
      await queryClient.invalidateQueries({ queryKey: STUDY_DOCUMENTS_QUERY_KEY });
      if (!isCurrent(request.scope)) return;
      onAnalysed(analysis);
    },
    onError: async (error, request) => {
      if (!isCurrent(request.scope)) return;
      const code = errorCode(error);
      if (code === 'AI_PRICE_CHANGED') await quote.refetch();
      if (code === 'INSUFFICIENT_POOL_BALANCE') await wallet.refetch();
      if (!isCurrent(request.scope)) return;
      if (code === 'WALLET_INSUFFICIENT') {
        const kind = (error as { details?: { kind?: unknown } }).details?.kind;
        setOutOfStars(kind === undefined || kind === 'balance');
      }
    },
  });
  // A new set of pages is a new request with its own key and no old error.
  const resetCreate = create.reset;
  useEffect(() => {
    resetCreate();
    setOutOfStars(false);
  }, [scope, resetCreate]);

  const needsPages =
    Boolean(onPrepare) && errorCode(quote.error) === 'STUDY_ANALYSIS_PAGE_NOT_UPLOADED';
  if (needsPages) {
    return (
      <button
        type="button"
        onClick={onPrepare}
        className={tone === 'secondary' ? 'btn-pill-secondary' : 'btn-pill-primary'}
      >
        <Sparkles className="mr-2 inline h-4 w-4" aria-hidden="true" />
        {actionLabel ?? 'Analyse'}
      </button>
    );
  }
  const stars = quote.data?.stars_total ?? null;
  const balance = wallet.data?.stars_balance;
  const lowBalance = stars !== null && typeof balance === 'number' && balance < stars;
  const unavailable = school || analysisUnavailable(quote.error);
  const reason = unavailable
    ? school
      ? ANALYSIS_NOT_FOR_SCHOOL
      : analysisErrorMessage(quote.error, ANALYSIS_NOT_FOR_SCHOOL)
    : quote.error
      ? analysisErrorMessage(quote.error, 'The price could not be checked. Try again.')
      : lowBalance
        ? liveExperience || institutionFunded
          ? 'Your teaching workspace needs more Stars. Your material is saved.'
          : ANALYSIS_LOW_BALANCE
        : create.error && errorCode(create.error) !== 'AI_PRICE_CHANGED'
          ? analysisErrorMessage(create.error, "Analysis didn't start. Try again.")
          : null;
  const priceChanged = errorCode(create.error) === 'AI_PRICE_CHANGED' && stars !== null;
  const disabled = unavailable || stars === null || lowBalance || create.isPending;
  const label = create.isPending
    ? 'Starting analysis…'
    : actionLabel
      ? `${actionLabel}${stars === null || unavailable ? '' : ` · ${stars}★`}`
      : analyseButtonLabel(refs.length, unavailable ? null : stars);

  return (
    <div className={compact ? 'flex flex-wrap items-center gap-3' : 'space-y-5'}>
      {!compact && pages.length > 0 && (
        <ol className="grid grid-cols-3 gap-3 sm:grid-cols-5" aria-label="Pages to analyse">
          {pages.map((page, index) => (
            <li key={`${page.document_id}:${page.page_number}`} className="text-center">
              <div className="flex aspect-[3/4] items-center justify-center overflow-hidden rounded-2xl border border-hairline bg-surface">
                {page.previewUrl ? (
                  <img
                    src={page.previewUrl}
                    alt={`Page ${index + 1}`}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <span className="text-xs font-bold text-slate2">Page {index + 1}</span>
                )}
              </div>
              <p className="mt-1 text-xs font-bold text-slate2">Page {index + 1}</p>
            </li>
          ))}
        </ol>
      )}

      <label className="block text-sm font-bold text-ink">
        Study notes
        <span className="my-2 block text-sm font-normal text-slate2">
          One confirmed analysis creates your notes, learning points and explanations automatically.
          Learning points do not cost extra Stars.
        </span>
        <select
          value={notesDepth}
          disabled={create.isPending}
          onChange={(event) => setNotesDepth(event.target.value as StudyNotesDepth)}
          className="ml-3 min-h-12 rounded-xl border border-hairline bg-canvas-pure px-3 font-normal"
        >
          <option value="summary">Summary</option>
          <option value="in_depth">In depth</option>
          <option value="complete">Complete</option>
        </select>
        <span className="mt-2 block text-xs font-normal text-slate2">
          {notesDepth === 'summary'
            ? 'Key ideas and a starting point.'
            : notesDepth === 'complete'
              ? 'Cover the selected readable pages. Unread pages stay marked.'
              : 'Explain the concepts with chapters and source references.'}{' '}
          Notes are included in the confirmed page price. Your file stays saved if generation fails.
        </span>
      </label>

      <div className={compact ? 'contents' : 'flex flex-wrap items-center gap-3'}>
        <button
          type="button"
          disabled={disabled}
          aria-describedby={reason || priceChanged ? reasonId : undefined}
          onClick={() => stars !== null && create.mutate({ scope, expectedStars: stars })}
          className={`${tone === 'secondary' ? 'btn-pill-secondary' : 'btn-pill-primary'} disabled:opacity-50`}
        >
          {create.isPending ? (
            <Loader2
              className="mr-2 inline h-4 w-4 animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
          ) : (
            <Sparkles className="mr-2 inline h-4 w-4" aria-hidden="true" />
          )}
          {label}
        </button>
        {onSave && (
          <button
            type="button"
            onClick={onSave}
            disabled={create.isPending}
            className="btn-pill-secondary disabled:opacity-50"
          >
            Just save it
          </button>
        )}
        {quote.isLoading && !school && (
          <span className="text-xs font-bold text-slate2" role="status">
            Checking the price…
          </span>
        )}
      </div>
      {(reason || priceChanged) && (
        <p
          id={reasonId}
          role={reason && !unavailable && !lowBalance ? 'alert' : 'status'}
          className="text-sm font-bold text-ink-soft"
        >
          {priceChanged ? `The price is now ${stars}★. Check it and try again.` : reason}
        </p>
      )}
      {!compact && !unavailable && (
        <p className="text-xs font-medium text-slate2">
          {/* F-14 (study-capture-fixes-prd.md): says what the analysis does and what Tutor does. */}
          AI reads your pages and lists the questions, key points and dates. It doesn&apos;t solve
          them. When you work on a question, your Tutor helps step by step. If a page can&apos;t be
          read, its Stars come back.
        </p>
      )}
      {outOfStars && (
        <OutOfStarsSheet
          body={OUT_OF_STARS_BODY}
          dismissLabel="Keep my material"
          onDismiss={() => setOutOfStars(false)}
        />
      )}
    </div>
  );
}
